import { Analytics } from '@cobliteam/analytics';
import Alert from '@cobliteam/ui/components/Alert';
import Button from '@cobliteam/ui/components/Button';
import CopyButton from '@cobliteam/ui/components/CopyButton';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import type { UIDataTypes, UIMessage, UITools } from 'ai';
import { useEffect, useState } from 'react';
import { type ScoreRequest, type ScoreResponse, useGetLangfuseScores, useSendLangfuseScore } from '../hooks/useLangfuseScore';
import DislikeFeedbackPopover from './elements/DislikeFeedbackPopover';
import { MessageActions, MessageToolbar } from './elements/Message';

interface AIChatActionsProps {
  message: UIMessage<UIDataTypes, UITools>;
  onRegenerate: () => void;
  /** Analytics source identifier */
  source?: string;
}

const AIChatActions = ({ message, onRegenerate, source }: AIChatActionsProps) => {
  const [positiveFeedback, setPositiveFeedback] = useState(false);
  const [negativeFeedback, setNegativeFeedback] = useState(false);
  const [comment, setComment] = useState<string | undefined>();

  const traceId = message.metadata?.traceId || message.id;
  const { data: feedbackData } = useGetLangfuseScores(traceId as string);
  const { mutate: scoreMutation, isPending } = useSendLangfuseScore();

  useEffect(() => {
    if (feedbackData && feedbackData.length > 0) {
      const userFeedback = feedbackData.find((f: ScoreResponse) => f.name === 'user-feedback');
      if (userFeedback) {
        setPositiveFeedback(userFeedback.value === 1);
        setNegativeFeedback(userFeedback.value === 0);
        setComment(userFeedback.comment);
      }
    }
  }, [feedbackData]);

  const getTextToCopy = () => {
    const part = message.parts.at(-1);
    if (part?.type === 'text' && part.text) return part.text;
    return '';
  };

  const handlePositiveFeedback = async () => {
    setPositiveFeedback(true);
    setNegativeFeedback(false);
    Analytics.track('AI - Chat - Response Feedback Given', { feedbackType: 'positive', source });

    if (positiveFeedback) return;

    const data = {
      name: 'user-feedback',
      dataType: 'CATEGORICAL',
      value: '1',
    } as const;

    const mutationData = message.metadata?.threadId ? { sessionId: message.metadata.threadId, ...data } : message.id ? { traceId: message.id, ...data } : null;

    if (mutationData) {
      scoreMutation(mutationData as ScoreRequest, {
        onSuccess: () => {
          Alert.success('Feedback enviado com sucesso!', { position: 'bottom-right' });
        },
        onError: () => {
          Alert.error('Erro ao enviar feedback. Tente novamente.', { position: 'bottom-right' });
        },
      });
    }
  };

  return (
    <MessageToolbar>
      <MessageActions>
        <Tooltip content='Regenerar resposta' asChild>
          <div>
            <Button
              size='sm'
              variant='ghost'
              icon={<Icon name='refresh-cw' className='size-4' />}
              onClick={() => {
                Analytics.track('AI - Chat - Response Regenerated', { source });
                onRegenerate();
              }}
            />
          </div>
        </Tooltip>
        <Tooltip content='Gostei da resposta' asChild>
          <div>
            <Button
              size='sm'
              variant={positiveFeedback ? 'contained' : 'ghost'}
              icon={<Icon name='thumbs-up' className='size-4' />}
              onClick={handlePositiveFeedback}
              loading={isPending && positiveFeedback}
              data-testid='ai-action-like'
            />
          </div>
        </Tooltip>
        <DislikeFeedbackPopover
          message={message}
          negativeFeedback={negativeFeedback}
          initialComment={comment}
          source={source}
          onFeedbackSent={(newComment) => {
            setNegativeFeedback(true);
            setPositiveFeedback(false);
            setComment(newComment);
          }}
        />
        <CopyButton
          variant='ghost'
          data-testid='ai-action-copy'
          textToCopy={getTextToCopy()}
          tooltipCopyText='Copiar resposta'
          tooltipCopiedText='Resposta copiada'
          copyToClipboard={async () => {
            Analytics.track('AI - Chat - Response Copied', { source });
            await navigator.clipboard.writeText(getTextToCopy());
            return Promise.resolve(true);
          }}
        />
      </MessageActions>
    </MessageToolbar>
  );
};

export default AIChatActions;
